/**
 * participantStatuses.js
 *
 * Single source of truth for participant and invite status vocabulary used
 * across the Referral Management System (RMS) frontend.
 *
 * This module defines the canonical lifecycle statuses for:
 *   1. Participants — people involved in a referral (school reps, guardians)
 *   2. Account invitations — email-based invites sent to participants
 *
 * These constants replace scattered inline string literals in PeoplePanel,
 * NominationField, ServiceUserDashboard, RmsPatientOverview, and referralStatus.
 *
 * Architecture context:
 *   - A participant doc tracks a person's relationship to a specific referral
 *     (their role, identity, and account-linking status).
 *   - An accountInvitation doc tracks the email invite that brings them onto
 *     the platform (sent via sign-in link, consumed on first login).
 *   - Participant status covers the invite lifecycle only — form progress is
 *     derived from formInstances, never stored on the participant doc.
 */

// ─── Participant Statuses ────────────────────────────────────────────────────
//
// Lifecycle:
//   Initiator path:   created at account setup → ACCOUNT_LINKED (immediate)
//   Nominee path:     NOMINATED → INVITE_SENT → ACCOUNT_LINKED
//                         ↘ SUPERSEDED (email change before linking)

export const PARTICIPANT_STATUS = {
  /** Nominated but no email invite dispatched yet. Initial state for nominees. */
  NOMINATED: 'nominated',
  /** Email sign-in link sent. User has not yet signed in. */
  INVITE_SENT: 'invite_sent',
  /** User signed in and UID linked. Terminal happy-path state. */
  ACCOUNT_LINKED: 'account_linked',
  /** Replaced by a newer nomination. Retained for audit only. */
  SUPERSEDED: 'superseded',
};

/** Statuses indicating the participant is still pending (not yet linked). */
export const PENDING_PARTICIPANT_STATUSES = [
  PARTICIPANT_STATUS.NOMINATED,
  PARTICIPANT_STATUS.INVITE_SENT,
];

// ─── Participant Roles ───────────────────────────────────────────────────────

export const PARTICIPANT_ROLE = {
  SCHOOL_USER: 'school_user',
  GUARDIAN: 'guardian',
  // Listed people only (Harbour teachers box): contact details on the record,
  // no account, no invite, and never any record access.
  TEACHER: 'teacher',
};

// ─── Invite Types ────────────────────────────────────────────────────────────
// Distinguishes the journey that brought a user to the platform.

export const INVITE_TYPE = {
  /** Started a referral via the public digital front door. */
  REFERRAL_INITIATOR: 'referral_initiator',
  /** Nominated by another participant (e.g. school nominates guardian). */
  NOMINEE: 'nominee',
};

// ─── Invite Statuses ─────────────────────────────────────────────────────────
//
// Lifecycle:
//   PENDING → CONSUMED     (user signs in and account linked)
//   PENDING → SUPERSEDED   (newer invite replaces this one)

export const INVITE_STATUS = {
  /** Invite created and email sent. User has not yet signed in. */
  PENDING: 'pending',
  /** User signed in and account linked. Terminal state. */
  CONSUMED: 'consumed',
  /** Replaced by a newer invite. Retained for audit only. */
  SUPERSEDED: 'superseded',
};
